/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState, useEffect, useRef } from "react";
import get from "lodash.get";
import { getGroupsData } from "../../layout-utils";
import { HeadBlock } from "../../atoms/head-block";
import { Attribute } from "../../attribute";
import { CardImage } from "../../atoms/card-image";
import { SubscribeButton } from "../../atoms/subscribe-button";
import { PlanDropdown } from "../../atoms/dropdown";
import { PriceDetails } from "../../atoms/price-details";
import { Loader } from "../../atoms/loader";
import { Description } from "../../atoms/description";
import { setMaxHeightForRefs } from "../../layout-utils";
import "./lavish.m.css";

const SubscriptionBlock = ({
  groups,
  subscriptionConfig = {},
  sso = {},
  member,
  defaultSubscriptionHandler,
  localizedTime,
  mode,
  sketchesHost,
  config
}) => {
  const [selectedSubscriptions, setSelectedSubscriptions] = useState({});
  const [activeDropdown, setActiveDropdown] = useState(null);

  let updateGrid = groups.length < 3 ? { gridTemplateColumns: `repeat(${groups.length}, minmax(auto, 372px))` } : {};
  const { descriptionTitle, showGroupImg = false, showOfferPrice = false } = subscriptionConfig;

  const toggleDropdown = (id) => {
    if (activeDropdown !== id) {
      setActiveDropdown(id);
    } else {
      setActiveDropdown(null);
    }
  };

  const groupRefs = groups.map(() => useRef(null));

  useEffect(() => {
    setMaxHeightForRefs(groupRefs);
  }, [groups]);

  return (
    <div styleName="background">
      {activeDropdown && <div styleName="overlay" onClick={() => setActiveDropdown(null)} />}
      <div styleName="subscription-wrapper" style={updateGrid}>
        {groups.map((group, index) => {
          const { name, description, image_url: imageUrl, id } = group;
          const initialPlan = get(groups, [index, "subscription_plans", 0]);
          const currentPlan = get(selectedSubscriptions, [groups[index].id], initialPlan);
          const priceCurrency = get(
            selectedSubscriptions,
            [groups[index].id, "price_currency"],
            get(initialPlan, ["price_currency"])
          );
          const priceCents = get(
            selectedSubscriptions,
            [groups[index].id, "price_cents"],
            get(initialPlan, ["price_cents"])
          );

          return (
            <div styleName="group" key={`group-${id}`}>
              <div styleName="tag-wrapper">
                <Attribute data={group} attributeName="Tag" />
              </div>
              <div styleName="title-wrapper">
                <div styleName="title" data-test-id="group-name" ref={groupRefs[index]}>
                  {name}
                </div>
                {showGroupImg && <CardImage title={name} imageUrl={imageUrl} type="group" />}
              </div>
              <PriceDetails
                priceCurrency={priceCurrency}
                priceCents={priceCents}
                showOfferPrice={showOfferPrice}
                localizedTime={localizedTime}
                plan={currentPlan}
                mode={mode}
              />
              <PlanDropdown
                group={group}
                initialPlan={initialPlan}
                currentPlan={currentPlan}
                selectedSubscriptions={selectedSubscriptions}
                setSelectedSubscriptions={setSelectedSubscriptions}
                mode={mode}
                toggleDropdown={toggleDropdown}
                activeDropdown={activeDropdown}
              />
              <SubscribeButton
                sso={sso}
                member={member}
                defaultSubscriptionHandler={defaultSubscriptionHandler}
                subscriptionConfig={subscriptionConfig}
                plan={currentPlan}
                mode={mode}
                sketchesHost={sketchesHost}
                config={config}
              />
              <Description data={description} descriptionTitle={descriptionTitle} mode={mode} type="group" />
            </div>
          );
        })}
      </div>
    </div>
  );
};

export const Lavish = (props) => {
  const groups = getGroupsData(props.currentPath, props.subscriptions);
  const subscriptionConfig = get(props, ["config", "pagebuilder-config", "subscription", "pageSetting"], {});

  if (!groups?.length || props?.isAccessTypeUserSetLoading) {
    return <Loader data={groups} subscriptionConfig={subscriptionConfig} />;
  } else {
    const enableDarkMode = get(props, ["enableDarkMode"], false);
    const mode = enableDarkMode ? "dark" : "light";
    const sso = get(props, ["config", "pagebuilder-config", "general", "sso"], {});
    const sketchesHost = get(props, ["config", "sketches-host"], '');
    return (
      <div styleName={`component-wrapper ${mode}`}>
        <HeadBlock subscriptionConfig={subscriptionConfig} mode={mode} />
        <SubscriptionBlock
          groups={groups}
          subscriptionConfig={subscriptionConfig}
          sso={sso}
          member={props?.member}
          defaultSubscriptionHandler={props?.defaultSubscriptionHandler}
          localizedTime={props?.localizedTime}
          mode={mode}
          sketchesHost={sketchesHost}
          config={props?.config}
        />
      </div>
    );
  }
};
